import { createSelector } from 'reselect'
import { findPostPurchaseCheckout, isBedbankItem, isInstantBookingLEHotelItem, isPurchaseCreditItem } from 'lib/checkout/checkoutUtils'
import { canShowAddFlightsForAccommodationItems, checkoutAccommodationOfferView } from './view/accommodation'
import { getFlightItems, getIsCharterFlightCheckout, getNonCharterFlightItems, isStandaloneFlights } from './view/flights'
import { getVillaViews } from 'checkout/selectors/view/villa'
import { getCanRedeemLuxPlusBenefits, getIsEnabledFlightsShowLuxPlusPriceToEveryone, getIsFlightsLuxPlusPricingEligibleSpoofedNonMember, getIsFreePreviewLuxPlusMember, getIsLuxPlusEnabled, getShouldShowFlightsBeforeLuxPlusUpsell } from 'luxPlus/selectors/featureToggle'
import { hasBundledAccommodationRate } from './hasBundledAccommodationRate'
import { getTourV1Items } from './view/tourv1'
import { checkIsExperienceGiftOfferOrderMode } from 'checkout/selectors/view/checkIsExperienceGiftOfferOrderMode'
import { getFunnelFeatures } from './funnel'
import { FLIGHTS_DISABLED_GEO_STATES } from 'constants/config/region'
import { getIsInternationalCheckoutFlowTreatment } from './internationalCheckoutFlow'
import { Experiments } from 'constants/experiments'
import { getExperimentEnabledDecision, getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import config from 'constants/config'
import {
  getHasBothTransferDirectionsBundled,
  isOneClickExperienceTransferEnabled,
} from './experienceSelectors'
import getUnifiedTransferView from './view/itemViews/getUnifiedTransferView'

const getHasPaidLuxPlusMembershipInAnyRegion = createSelector(
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => state.auth.account.luxPlus.member.subscription.item,
  (enabled, memberSubscriptionItem): boolean => {
    return enabled &&
      (memberSubscriptionItem?.status === 'ACTIVE' || memberSubscriptionItem?.status === 'AWAITING_RENEWAL') &&
      memberSubscriptionItem?.tier !== 'free_preview'
  },
)

const getHasFlightsLuxPlusUpsellContext = createSelector(
  (state: App.State) => getNonCharterFlightItems(state),
  (state: App.State) => canShowAddFlightsForAccommodationItems(state),
  (flightItems, canShowAddFlights): boolean => flightItems.length > 0 || canShowAddFlights,
)

export const isLuxPlusCheckoutStepAvailable = createSelector(
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => getCanRedeemLuxPlusBenefits(state),
  (state: App.State) => getHasPaidLuxPlusMembershipInAnyRegion(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => checkIsExperienceGiftOfferOrderMode(state),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => state.checkout.cart.items.some(isPurchaseCreditItem),
  (state: App.State) => getIsFreePreviewLuxPlusMember(state),
  (state: App.State) => getIsFlightsLuxPlusPricingEligibleSpoofedNonMember(state),
  (state: App.State) => getIsEnabledFlightsShowLuxPlusPriceToEveryone(state),
  (state: App.State) => getHasFlightsLuxPlusUpsellContext(state),
  (enabled, canRedeemLuxPlusBenefits, hasPaidLuxPlusMembershipInAnyRegion, isGift, isPostPurchase, isExperienceGiftOfferOrderMode, funnelFeatures, hasPurchaseCreditItem, isFreePreviewLuxPlusMember, isFlightsLuxPlusPricingEligibleSpoofedNonMember, isFlightsShowLuxPlusPriceToEveryoneEnabled, hasFlightsLuxPlusUpsellContext): boolean => {
    if (hasPurchaseCreditItem) return false
    const shouldSeeUpsell = !canRedeemLuxPlusBenefits && !hasPaidLuxPlusMembershipInAnyRegion
    const isFunnelEligible = funnelFeatures.luxPlus ||
      isFreePreviewLuxPlusMember ||
      isFlightsLuxPlusPricingEligibleSpoofedNonMember ||
      (isFlightsShowLuxPlusPriceToEveryoneEnabled && hasFlightsLuxPlusUpsellContext)
    return !!(enabled && shouldSeeUpsell && (isExperienceGiftOfferOrderMode || !isGift) && !isPostPurchase && isFunnelEligible)
  },
)

export const isFlightsPendingToAddToCart = createSelector(
  (state: App.State) => getNonCharterFlightItems(state),
  (flightItems): boolean => {
    return flightItems.length > 0 &&
      flightItems.some(item => {
        if (item.fareType === 'return') {
          return !item.flights[0] || !item.flights[1]
        } else if (item.fareType === 'oneWay') {
          return !item.flights[0]
        }
      })
  },
)

export const hasExistingFlightItems = createSelector(
  (state: App.State) => getFlightItems(state),
  (flightItems): boolean => flightItems.length > 0,
)

export const hasIncompleteTourItem = createSelector(
  (state: App.State) => getTourV1Items(state),
  item => item.some(item => !item.packageId || !item.startDate || !item.endDate),
)

export const hasInsuranceItem = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder?.insuranceItems,
  (items) => {
    if (!items?.length) {
      return false
    }

    return items.some(item => item.status !== 'cancelled')
  },
)

export const isExperiencesSupportedToAddToCart = createSelector(
  (state: App.State) => getIsInternationalCheckoutFlowTreatment(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getVillaViews(state),
  (state: App.State) => hasBundledAccommodationRate(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => getExperimentEnabledDecision(getOptimizelyExperimentVariation(state, Experiments.standaloneFlightUpsellExperiences.id)),
  (isInternationalCheckoutFlowTreatment, accommodationViews, villaViews, hasBundledRate, postPurchase, funnelFeatures, flightItems, standaloneFlightUpsellExperiences): boolean => {
    if (isInternationalCheckoutFlowTreatment) {
      return false
    }

    if (!funnelFeatures.experiences) {
      return false
    }

    // Experience-only BNBL should skip ExperiencesPage (only used for adding experiences to hotel checkout)
    const hasAccommodation = accommodationViews.data.length > 0 || villaViews.data.length > 0
    if (postPurchase === 'select-date' && !hasAccommodation) {
      return false
    }

    if (!postPurchase || postPurchase === 'select-date') {
      if (hasAccommodation) {
        return accommodationViews.data.every(view => view.itemViews.every(itemView => isInstantBookingLEHotelItem(itemView.item) || isBedbankItem(itemView.item))) ||
          villaViews.data.length > 0
      }

      // Upsell experience on flight standalone flow
      if (flightItems.length) {
        return config.EXPERIENCES_STANDALONE_FLIGHTS_INFLOW_ENABLED && standaloneFlightUpsellExperiences
      }
    }

    return false
  },
)

export const isAirportTransfersStepAvailable = createSelector(
  (state: App.State) => isOneClickExperienceTransferEnabled(state),
  (state: App.State) => getUnifiedTransferView(state),
  (state: App.State) => getHasBothTransferDirectionsBundled(state),
  (state: App.State) => getFunnelFeatures(state),
  (
    isOneClickTransferEnabled,
    unifiedTransferView,
    hasBothDirectionsBundled,
    funnelFeatures,
  ): boolean => {
    if (!config.EXPERIENCE_TRANSFER_DEDICATED_PAGE || !funnelFeatures.experiences) return false

    const hasLoyaltyTransfers = unifiedTransferView.hasRequiredData &&
      unifiedTransferView.data.transferItems.length > 0 &&
      unifiedTransferView.data.transferType !== 'HOME_TO_AIRPORT'

    return isOneClickTransferEnabled &&
      !hasLoyaltyTransfers &&
      !hasBothDirectionsBundled
  },
)

export const isLuxPlusAvailableToAddToCart = createSelector(
  (state: App.State) => isLuxPlusCheckoutStepAvailable(state),
  (state: App.State) => state.auth.account.luxPlus.member.disableMembership,
  (isLuxPlusStepAvailable, disableMembership): boolean => isLuxPlusStepAvailable && !disableMembership,
)

/**
 * Maldives charter bundles run the LuxPlus+ upsell BEFORE feeder flight selection,
 * inverting the `getShouldShowFlightsBeforeLuxPlusUpsell` ordering for this funnel only.
 *
 * Deliberately keyed on the charter item alone, NOT on whether a feeder is still pending:
 * a pending-based predicate would flip to false the moment the feeder legs are chosen, and
 * the post-flights branch in `getNextCheckoutPage` would then send the customer back to a
 * step they have already seen.
 *
 * `App.CharterBundleRoute` only covers Maldives routes today. If a non-Maldives charter
 * route is ever added, narrow this to the campaign-tag check in `view/flights.ts:485`.
 *
 * Both the routing (`getNextCheckoutPage`) and the progress stepper
 * (`checkoutStepsSelectors`) read the ordering decision from here, so they cannot drift.
 */
export const getShouldShowLuxPlusBeforeCharterFeederFlights = getIsCharterFlightCheckout

export const isLuxPlusStandaloneStepAvailable = createSelector(
  (state: App.State) => isLuxPlusCheckoutStepAvailable(state),
  (state: App.State) => getIsInternationalCheckoutFlowTreatment(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => getIsFreePreviewLuxPlusMember(state),
  (state: App.State) => getShouldShowFlightsBeforeLuxPlusUpsell(state),
  (state: App.State) => getIsFlightsLuxPlusPricingEligibleSpoofedNonMember(state),
  (isLuxPlusAvailable, isInternationalCheckoutFlowTreatment, standaloneFlights, isFreePreviewLuxPlusMember, shouldShowFlightsBeforeLuxPlusUpsell, isFlightsLuxPlusPricingEligibleSpoofedNonMember): boolean =>
    isLuxPlusAvailable && !isInternationalCheckoutFlowTreatment &&
    (!standaloneFlights || isFreePreviewLuxPlusMember || shouldShowFlightsBeforeLuxPlusUpsell || isFlightsLuxPlusPricingEligibleSpoofedNonMember),
)

export const isAddFlightsStepAvailable = createSelector(
  (state: App.State) => getIsInternationalCheckoutFlowTreatment(state),
  (state: App.State) => canShowAddFlightsForAccommodationItems(state),
  (state: App.State) => isFlightsPendingToAddToCart(state),
  (state: App.State) => hasExistingFlightItems(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => state.geo.geoStateName,
  (isInternationalCheckoutFlowTreatment, canShowAddFlightsForAccommodationItems, hasFlightsPending, hasExistingFlightItems, isGift, isPostPurchase, funnelFeatures, geoStateName): boolean => {
    if (isInternationalCheckoutFlowTreatment) {
      return false
    }

    if (geoStateName && FLIGHTS_DISABLED_GEO_STATES.includes(geoStateName)) return false

    const hasValidFlightContext = hasFlightsPending || (canShowAddFlightsForAccommodationItems && !hasExistingFlightItems)
    return hasValidFlightContext && !isGift && !isPostPurchase && funnelFeatures.flights
  },
)

export const hasBundledFlightItem = createSelector(
  (state: App.State) => getNonCharterFlightItems(state),
  (flightItems): boolean => flightItems.some(item => !!item.bundledItemIds?.length),
)

export const isBundledAddFlightsStepAvailable = createSelector(
  (state: App.State) => getIsCharterFlightCheckout(state),
  (state: App.State) => getIsInternationalCheckoutFlowTreatment(state),
  (state: App.State) => hasBundledFlightItem(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => state.geo.geoStateName,
  (isCharterFlightCheckout, isInternationalCheckoutFlowTreatment, hasBundledFlightItem, isGift, isPostPurchase, funnelFeatures, geoStateName): boolean => {
    if (isCharterFlightCheckout) return false

    if (isInternationalCheckoutFlowTreatment) {
      return false
    }

    if (geoStateName && FLIGHTS_DISABLED_GEO_STATES.includes(geoStateName)) return false

    return hasBundledFlightItem && !isGift && !isPostPurchase && funnelFeatures.flights
  },
)

export const isAddFlightsStepRouteAvailable = createSelector(
  (state: App.State) => isAddFlightsStepAvailable(state),
  (state: App.State) => isBundledAddFlightsStepAvailable(state),
  (isAddFlightsAvailable, isBundledFlightsAvailable): boolean =>
    isAddFlightsAvailable || isBundledFlightsAvailable,
)
